import { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Grid,
  Popover,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CasinoRounded from '@mui/icons-material/CasinoRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ColorizeRounded from '@mui/icons-material/ColorizeRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import { HexColorPicker } from 'react-colorful';
import { contrastRatio, transition } from '@/theme';
import { citySlug, schemeFor } from '../format';
import { SAMPLE_BRANDS, type BrandState } from '../brands';
import { PhoneMockup } from './PhoneMockup';

/** Base domain of the platform, as seen from this page (enc.example.com → example.com). */
function baseDomain() {
  const parts = window.location.hostname.split('.');
  return parts.length > 2 ? parts.slice(1).join('.') : window.location.hostname;
}

interface BrandLabProps {
  value: BrandState;
  onChange: (value: BrandState) => void;
}

/**
 * "Type your name, pick a color": the student app restyles itself live. It is
 * the real palette engine of the product (Material 3 tones with guaranteed
 * contrast), not a picture.
 */
export function BrandLab({ value, onChange }: BrandLabProps) {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [spin, setSpin] = useState(0);
  const address = `${citySlug(value.name) || 'suamarca'}.${baseDomain()}`;

  const surprise = () => {
    const others = SAMPLE_BRANDS.filter((b) => b.color !== value.color);
    const next = others[Math.floor(Math.random() * others.length)];
    setSpin((n) => n + 1);
    onChange({ ...value, ...next, radius: [8, 16, 24][Math.floor(Math.random() * 3)] });
  };

  return (
    <Grid container spacing={{ xs: 4, md: 6 }} sx={{ alignItems: 'center' }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack spacing={3}>
          <TextField
            label="Nome da sua marca"
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value.slice(0, 24) })}
            helperText="Escreva o nome do seu estúdio, consultoria ou o seu próprio nome."
          />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
              Cor principal
            </Typography>
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1.25 }}>
              {SAMPLE_BRANDS.map((brand) => {
                const selected = brand.color.toLowerCase() === value.color.toLowerCase();
                return (
                  <Tooltip key={brand.color} title={brand.name}>
                    <ButtonBase
                      aria-label={`Usar a cor de ${brand.name}`}
                      aria-pressed={selected}
                      onClick={() => onChange({ ...value, ...brand })}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        bgcolor: brand.color,
                        color: '#fff',
                        boxShadow: selected
                          ? `0 0 0 3px var(--app-palette-background-paper), 0 0 0 6px ${brand.color}`
                          : 'none',
                        transition: transition(
                          ['box-shadow', 'transform'],
                          'medium1',
                          'springFast',
                        ),
                        '&:hover': { transform: 'scale(1.08)' },
                      }}
                    >
                      {selected && <CheckRounded />}
                    </ButtonBase>
                  </Tooltip>
                );
              })}
              <Tooltip title="Escolher outra cor">
                <ButtonBase
                  aria-label="Escolher outra cor"
                  onClick={(e) => setPickerAnchor(e.currentTarget)}
                  sx={(theme) => ({
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    border: `2px dashed ${theme.vars.palette.divider}`,
                    color: theme.vars.palette.text.secondary,
                  })}
                >
                  <ColorizeRounded fontSize="small" />
                </ButtonBase>
              </Tooltip>
            </Stack>
            <Popover
              open={Boolean(pickerAnchor)}
              anchorEl={pickerAnchor}
              onClose={() => setPickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 2 }}>
                <HexColorPicker
                  color={value.color}
                  onChange={(color) => onChange({ ...value, color: color.toUpperCase() })}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  Qualquer cor funciona: o contraste é ajustado sozinho.
                </Typography>
              </Box>
            </Popover>
          </Box>
          <Box>
            <Typography variant="subtitle2">Cantos</Typography>
            <Slider
              value={value.radius}
              min={2}
              max={28}
              step={1}
              onChange={(_, radius) => onChange({ ...value, radius: radius as number })}
              marks={[
                { value: 4, label: 'Retos' },
                { value: 16, label: 'Suaves' },
                { value: 26, label: 'Redondos' },
              ]}
              aria-label="Arredondamento dos cantos"
              sx={{ mx: 1, width: 'calc(100% - 16px)' }}
            />
          </Box>
          <Box
            sx={(theme) => ({
              p: 1.5,
              borderRadius: `${Number(theme.shape.borderRadius)}px`,
              bgcolor: theme.vars.palette.background.subtle,
              border: `1px solid ${theme.vars.palette.divider}`,
            })}
          >
            <Typography variant="caption" color="text.secondary">
              Seu endereço
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.25 }}>
              <LockRounded sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography
                sx={{ fontFamily: 'monospace', fontSize: '0.95rem', wordBreak: 'break-all' }}
              >
                https://<b>{address.split('.')[0]}</b>.{address.split('.').slice(1).join('.')}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Ou o seu domínio próprio, como app.suamarca.com.br.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="tonal"
              startIcon={
                <CasinoRounded
                  key={spin}
                  sx={{
                    animation: spin ? 'dice 600ms cubic-bezier(0.34, 1.56, 0.64, 1)' : undefined,
                    '@keyframes dice': { from: { transform: 'rotate(-270deg) scale(0.6)' } },
                  }}
                />
              }
              onClick={surprise}
            >
              Surpreenda-me
            </Button>
            <Chip
              size="small"
              variant="outlined"
              label={`Contraste do botão: ${contrastLabel(value.color)}`}
            />
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <PhoneMockup brandName={value.name} color={value.color} radius={value.radius} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          Isto não é um print: é o mesmo motor de cores do app, funcionando ao vivo.
        </Typography>
      </Grid>
    </Grid>
  );
}

function contrastLabel(color: string) {
  // The tone picked by the palette engine always reaches at least AA with white text.
  const ratio = contrastRatio(schemeFor(color).light.primary.main, '#FFFFFF');
  return `${ratio.toFixed(1)}:1 (${ratio >= 7 ? 'AAA' : 'AA'})`;
}
